import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import ErrorMessage from '@components/Common/ErrorMessage';
import DetailSection from '@components/Common/DetailSection';
import FormField from '@components/Common/FormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import type { RootState } from '@store/rootReducer';
import { useSelector } from 'react-redux';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';

type JobMetadata = {
  _id: string;
  type: string;
  name: string;
  isActive: boolean;
  sortOrder?: number;
};

const COLUMNS = [
  { type: 'SKILL', title: 'Skills', singular: 'skill' },
  { type: 'CATEGORY', title: 'Categories', singular: 'category' },
  { type: 'INDUSTRY', title: 'Industries', singular: 'industry' },
  /*
   * The areas that count as Mumbai when HR filters or charts candidates by city: someone who
   * writes "Andheri" or "Thane" is in Mumbai. Seeded by the backend once; this is where the list
   * grows. Another city gets its own CITY_AREA:<CITY> column the same way.
   */
  { type: 'CITY_AREA:MUMBAI', title: 'Mumbai areas', singular: 'area', empty: 'No Mumbai areas yet.' },
  /*
   * The cities Mintly serves: the site's footer, the "Cities we serve" section on the employer
   * pages, and the sitemap. Order here is order there. Removing a city takes it off those lists
   * but keeps its /jobs-in-<city> page, so nothing indexed starts to 404.
   */
  { type: 'CITY', title: 'Cities we serve', singular: 'city', empty: 'No cities yet.' },
] as const;

/** The first three are job vocabularies; the city pair sits on its own row at desktop width. */
const gridSize = (type: string) =>
  type === 'CITY' || type.startsWith('CITY_AREA:') ? { xs: 12, sm: 6, lg: 6 } : { xs: 12, sm: 6, lg: 4 };

const JobMetadataPage: React.FC = () => {
  const { showToast } = useToast();
  const actorEmail = useSelector(
    (state: RootState) => state.loginUserProfile.profile?.personalDetails?.email,
  );
  const actor = actorEmail || 'control-center';

  const [items, setItems] = useState<JobMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  // A toast is a notification, not an error state: it is gone in four seconds, and a page
  // that failed to load then just looks empty with no way to tell why or retry.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<JobMetadata | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      setLoadError(null);
      const response = await axios.get('/api/job-metadata');
      // `|| []` guards null and undefined but not a non-array, and everything below assumes an
      // array: `items.forEach` two functions down throws, React unmounts the page into the error
      // boundary, and the whole screen is lost. A 200 carrying an unexpected shape — an error
      // envelope, or a paginated `{ content: [] }` — is enough to trigger it. Same failure the
      // /api/queue/ fixture comment records, so the same guard.
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Could not load job metadata.';
      setLoadError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    // From COLUMNS, so a column added there is never silently dropped here.
    const byType: Record<string, JobMetadata[]> = Object.fromEntries(COLUMNS.map((c) => [c.type, []]));
    items.forEach((it) => {
      if (!byType[it.type]) return;
      if (term && !it.name.toLowerCase().includes(term)) return;
      byType[it.type].push(it);
    });
    Object.values(byType).forEach((list) =>
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
    );
    return byType;
  }, [items, search]);

  const onAdd = async (type: string) => {
    const name = (drafts[type] || '').trim();
    if (!name) return;
    const existing = items.filter((it) => it.type === type);
    if (existing.some((it) => it.name.toLowerCase() === name.toLowerCase())) {
      showToast(`"${name}" already exists`, 'error');
      return;
    }
    const nextOrder = existing.reduce((max, it) => Math.max(max, it.sortOrder ?? 0), 0) + 1;
    try {
      await axios.post('/api/job-metadata', { type, name, isActive: true, sortOrder: nextOrder, createdBy: actor });
      showToast(`${name} added`, 'success');
      setDrafts((prev) => ({ ...prev, [type]: '' }));
      await loadItems();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to add', 'error');
    }
  };

  const onToggleActive = async (item: JobMetadata) => {
    setBusyId(item._id);
    try {
      await axios.put(`/api/job-metadata/${item._id}`, { isActive: !item.isActive, updatedBy: actor });
      setItems((prev) => prev.map((it) => (it._id === item._id ? { ...it, isActive: !it.isActive } : it)));
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onSaveRename = async (item: JobMetadata) => {
    const name = editValue.trim();
    if (!name || name === item.name) {
      setEditingId(null);
      return;
    }
    setBusyId(item._id);
    try {
      await axios.put(`/api/job-metadata/${item._id}`, { name, updatedBy: actor });
      setItems((prev) => prev.map((it) => (it._id === item._id ? { ...it, name } : it)));
      setEditingId(null);
      showToast('Renamed', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to rename', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async () => {
    if (!pendingDelete) return;
    setBusyId(pendingDelete._id);
    try {
      await axios.delete(`/api/job-metadata/${pendingDelete._id}`);
      setItems((prev) => prev.filter((it) => it._id !== pendingDelete._id));
      showToast('Deleted', 'success');
      setPendingDelete(null);
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to delete', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ width: 320, maxWidth: '100%' }}>
          <FormField required={false} label="Search" name="search" value={search} placeholder="Filter skills, categories, cities…" onChange={(_, v) => setSearch(v)} />
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={() => loadItems()} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2}>
        {COLUMNS.map((col) => {
          const list = grouped[col.type] || [];
          const activeCount = list.filter((it) => it.isActive).length;
          return (
            <Grid key={col.type} size={gridSize(col.type)}>
              {/* No bottom margin of its own: the grid's 16px gap spaces rows as well as columns. */}
              <DetailSection
                sx={{ mb: 0, height: '100%' }}
                title={col.title}
                headerAction={<Chip size="small" label={`${activeCount} active / ${list.length}`} />}
              >
                {/* The shared field row: flex-end on the row, so the button needs neither its own
                    alignSelf nor a literal height guessing at the input's. */}
                <Box className="cc-field-row cc-field-row--stacked">
                  <Box sx={{ flex: 1 }}>
                    <FormField
                      required={false}
                      label={`Add ${col.singular}`}
                      name={`add-${col.type}`}
                      value={drafts[col.type] || ''}
                      onChange={(_, v) => setDrafts((prev) => ({ ...prev, [col.type]: v }))}
                      textFieldProps={{
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            onAdd(col.type);
                          }
                        },
                      }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => onAdd(col.type)}
                    disabled={!(drafts[col.type] || '').trim()}
                    sx={{ minWidth: 0, px: 1.5 }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Box>

                <Box sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
                  {loadError && list.length === 0 && (
                    <ErrorMessage message={loadError} onRetry={loadItems} />
                  )}
                  {loading && list.length === 0 && (
                    <SkeletonLoader rows={4} />
                  )}
                  {!loading && list.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {search ? 'No matches.' : ('empty' in col ? col.empty : `No ${col.title.toLowerCase()} yet.`)}
                    </Typography>
                  )}
                  {list.map((item) => {
                    const isEditing = editingId === item._id;
                    const isBusy = busyId === item._id;
                    return (
                      <Box
                        key={item._id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          opacity: item.isActive ? 1 : 0.55,
                        }}
                      >
                        {isEditing ? (
                          <>
                            <Box sx={{ flex: 1 }}>
                              <FormField
                                required={false}
                                label=""
                                name={`edit-${item._id}`}
                                value={editValue}
                                onChange={(_, v) => setEditValue(v)}
                                textFieldProps={{
                                  autoFocus: true,
                                  onKeyDown: (e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') onSaveRename(item);
                                    if (e.key === 'Escape') setEditingId(null);
                                  },
                                }}
                              />
                            </Box>
                            <Tooltip title="Save">
                              <IconButton size="small" color="success" onClick={() => onSaveRename(item)} disabled={isBusy}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={() => setEditingId(null)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                              {item.name}
                            </Typography>
                            <Tooltip title={item.isActive ? 'Click to deactivate' : 'Click to activate'}>
                              <Chip
                                size="small"
                                label={item.isActive ? 'Active' : 'Inactive'}
                                color={item.isActive ? 'success' : 'default'}
                                variant={item.isActive ? 'filled' : 'outlined'}
                                onClick={() => onToggleActive(item)}
                                disabled={isBusy}
                                sx={{ cursor: 'pointer' }}
                              />
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingId(item._id);
                                  setEditValue(item.name);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setPendingDelete(item)} disabled={isBusy}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </DetailSection>
            </Grid>
          );
        })}
      </Grid>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this item?"
        body={
          <>
            {pendingDelete?.type === 'CITY' ? (
              <>
                <strong>{pendingDelete?.name}</strong> comes off the footer and the cities we
                serve. Its jobs page stays, so search results that link to it still work.
              </>
            ) : (
              <>
                <strong>{pendingDelete?.name}</strong> is removed from job forms and from the
                filters that use it. Jobs already carrying the value keep it.
              </>
            )}
          </>
        }
        busy={busyId === pendingDelete?._id}
        onConfirm={onDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default JobMetadataPage;
